import {
  DCPCarProductEquipmentDTO,
  DCPFinancialInformationDTO,
  DCPProductTypeDTO,
  DCPValueUnitDTO,
  ExtendedSMSProduct,
  SMSClassification,
  SMSFeature,
  SMSImage,
  SMSPrice,
  SMSStock,
} from '../../models/shared/ocb';
import {
  EmicoAttribute,
  EmicoItem,
  FormattedData,
  Label,
  LabelDescription,
  ProductDetail,
  Url,
} from '../../models/product';
import get from 'lodash/get';

type MapperConfig = {
  i18n: {
    lang: string;
    getTranslationUnitFallback(value: number, key: string, displayName: string): string;
    getTranslationFallback(key: string, displayName: string): string;
    getEngineDescription(
      combustionPower: DCPValueUnitDTO,
      combustionPowerSecondary: DCPValueUnitDTO,
      electricPower: DCPValueUnitDTO,
      electricPowerSecondary: DCPValueUnitDTO,
    ): string;
  };
};

export function createProductDetail(
  data: ExtendedSMSProduct,
  { i18n: { lang, getEngineDescription, getTranslationUnitFallback, getTranslationFallback } }: MapperConfig,
): ProductDetail {
  const emicoParsed = JSON.parse(data.localEmissionAndConsumptionData || '{}');

  const emicoData = {
    nefz: mapEmicoData(emicoParsed?.uidata?.overlay, lang),
    wltp: mapEmicoData(emicoParsed?.uidata, lang),
  };

  const valueFormat = (name: string, unit?: string): any => {
    if (!data[name] || data[name]?.value === 0) return null;

    const displayName = data[name]?.displayValue
      ? data[name]?.displayValue
      : data[name]?.value + ' ' + unit ?? data[name]?.unit;

    return getTranslationUnitFallback(data[name]?.value, `dcp.unit.${data[name].unitCode}`, displayName);
  };

  /**
   *
   * @param param0 as an object made by {formattedValue, label, value} properties
   * @returns a FormattedData object
   */
  const createFormattedData = <T extends string | number | undefined>({
    formattedValue = undefined,
    label = undefined,
    value = undefined,
  }: FormattedData<T>): FormattedData<T> => ({
    formattedValue,
    label,
    value,
  });

  /**
   *
   * @param highlight1 string
   * @param highlight2 string
   * @param highlight3 string
   * @returns the list of existing and non-empty highlights as an array of labels
   */
  const createHighlights = (highlight1 = '', highlight2 = '', highlight3 = ''): Label[] => {
    const highlights = [] as Label[];

    if (highlight1) {
      highlights.push(createLabel(highlight1));
    }

    if (highlight2) {
      highlights.push(createLabel(highlight2));
    }

    if (highlight3) {
      highlights.push(createLabel(highlight3));
    }

    return highlights;
  };

  /**
   *
   * @param badges as an array of {name, description} objects
   * @returns the list of {label, description} objects
   */
  const createBadges = (badges: unknown[] = []): LabelDescription[] => {
    const mappedBadges = [] as LabelDescription[];

    badges.forEach((badge: any) => {
      const { name = '', description = '' } = badge;

      if (name !== '') {
        mappedBadges.push(createLabelDescription({ label: name, description }));
      }
    });

    return mappedBadges;
  };

  /**
   *
   * @param label string
   * @returns the object with a label property
   */
  const createLabel = (label = ''): Label => {
    return {
      label,
    };
  };

  /**
   *
   * @param url string | undefined
   * @returns the object with an url property
   */
  const createUrl = (url: string | undefined): Url => ({
    url: url ?? '',
  });

  /**
   *
   * @param param0 as an object of {label and description}
   * @returns the LabelDescription object
   */
  const createLabelDescription = ({ label = '', description = '' }): LabelDescription => ({
    label,
    description,
  });

  const getClassificationFeatureValue = (name: string) =>
    (
      get(data, 'classifications[0].features')?.find((classification) =>
        classification.code?.endsWith(name),
      ) as Required<SMSFeature>
    )?.featureValues[0].value;

  const exteriorRegexp = /(?:POV=BE)([0-9]{3})/;

  const powerTranslation =
    data.powerTranslation ||
    getEngineDescription(
      data.combustionPower ?? {},
      data.combustionPowerSecondary ?? {},
      data.electricPower ?? {},
      data.electricPowerSecondary ?? {},
    );

  const createOptionalFormattedData = (
    productParam: Partial<FormattedData<string>>,
  ): FormattedData<string> | undefined => {
    if (!productParam.formattedValue && !productParam.value) {
      return;
    }

    return productParam;
  };

  const translateProductEnum = (key: string, code: string, displayName?: string) =>
    getTranslationFallback(`dcp.product.attribute.${key}.value.${code}`, displayName as string);

  return {
    baseId: data.baseId!,
    classifications: data.classifications as Required<SMSClassification>[],
    carEquipments: data.carEquipments as Required<DCPCarProductEquipmentDTO>[],
    steeringWheel: data.steeringWheel!,
    price: data.price as Required<SMSPrice>,
    colorVariants: data.colorVariants!,
    engineSize: data.engineSize!,
    engineType: data.engineType!,
    dcpProductType: data.dcpProductType as Required<DCPProductTypeDTO>,
    discountPrice: data.discountPrice!,
    division: data.division!,
    fiscalValue: data.fiscalValue as Required<DCPFinancialInformationDTO>,
    frontTyreSize: data.frontTyreSize!,
    localEmissionAndConsumptionData: data.localEmissionAndConsumptionData!,
    model: data.model!,
    monthlyRate: data.monthlyRate as Required<DCPFinancialInformationDTO>,
    numberOfReviews: data.numberOfReviews!,
    pointOfServices: data.pointOfServices!,
    pointOfServiceCode: data.pointOfServiceCode!,
    pointOfServiceDisplayName: data.pointOfServiceDisplayName!,
    pointOfServiceFormattedAddress: data.pointOfServiceFormattedAddress!,
    wholeSale: data.wholeSale!,
    primaryImage: data.primaryImage as SMSImage,
    rearTyreSize: data.rearTyreSize!,
    reservationFee: data.reservationFee as SMSPrice,
    source: data.source!,
    stock: data.stock as SMSStock,
    volume: data.volume!,
    assortment: 'vehicle',
    characteristics: {
      highlights: createHighlights(data.highlight1, data.highlight2, data.highlight3),
      packages: undefined,
      lines: undefined,
      componentGroups: undefined,
      badges: createBadges(data.badges),
    },
    loadingSpaceLength: valueFormat('loadingSpaceLength'),
    loadingSpaceWidth: valueFormat('loadingSpaceWidth'),
    loadingSpaceHeight: valueFormat('loadingSpaceHeight'),
    loadingSpaceVolume: valueFormat('loadingSpaceVolume'),
    height: valueFormat('height'),
    identification: {
      code: data.code!,
      commissionNumber: data.commissionNumber!,
      vin: data.vin,
      variantId: data.variantId,
      productType: data.productType ?? data.dcpProductType?.name,
      productId: data.vehicleId,
    },
    engineSizeInCcm: valueFormat('engineSize'),
    mileage: valueFormat('mileage'),
    color: {
      group: data.colorText ?? getClassificationFeatureValue('.color_text'),
      polish: data.colorPolish,
      text: data.colorText ?? getClassificationFeatureValue('.color_text'),
      image: createUrl(data?.colorUrl),
    },
    upholstery: {
      group: data.upholsteryGroup,
      polish: data.upholsteryPolish,
      text: data.upholsteryText ?? getClassificationFeatureValue('.upholstery_text'),
      image: createUrl(data.upholsteryUrl),
    },
    images: {
      exterior: (get(data, 'images')?.filter((image) => {
        if (image.url && exteriorRegexp.test(image.url)) {
          return createUrl(image.url);
        }
      }) as Url[]) ?? [createUrl(get(data, 'primaryImage')?.url)],
      interior:
        (get(data, 'images')?.filter((image) => {
          if (image.url && !exteriorRegexp.test(image.url)) {
            return createUrl(image.url);
          }
        }) as Url[]) || [],
      default:
        (get(data, 'images')?.filter((image) => {
          if (image.url && exteriorRegexp.test(image.url)) {
            return createUrl(image.url);
          }
        })[0] as Url) || undefined,
    },
    length: valueFormat('length'),
    combustionEngineSize: valueFormat('length'),
    fuelTankCapacity: valueFormat('fuelTankCapacity'),
    powerInKw: data.powerInKw!,
    listPrice: data.listPrice as Required<SMSPrice>,
    // vehicleSearchUrl: get(data, 'bfsProductConfiguration.bfsDetailUrl'),
    vehicleModel: {
      name: data.name!,
      baumuster: data.baumuster!,
      nationalSalesType: undefined,
      modelYearCode: data.modelYearCode!,
      modelYear: data.modelYear!,
      changeYearCode: undefined,
      bodyType: createFormattedData<string>({
        value: data?.bodyType?.code,
        formattedValue: getTranslationFallback(
          `dcp.product.attribute.bodytype.value.${data?.bodyType?.code}`,
          data?.bodyType?.name as string,
        ),
      }),
      vehicleClass: createFormattedData<string>({}),
      brand: createFormattedData<string>({ value: data.brand }),
      productGroup: createFormattedData<string>({ value: data.division }),
    },
    summary: (() => {
      let summary = '';

      if (data.powerInKw) {
        summary += `${data.powerInKw} kW (${Math.round(Number(data.powerInKw) * 1.36)} hp),`;
      }

      if (data.fuelType) {
        summary += ` ${data.fuelType},`;
      }

      if (data.transmission) {
        summary += ` ${data.transmission}`;
      }

      if (data.summary?.substr(-1, 1) === ',') {
        summary = summary.substr(0, summary.length - 1);
      }

      return summary;
    })(),
    technicalInformation: {
      topSpeed: {
        value: data.maxSpeed?.value,
        unit: data.maxSpeed?.unit,
        formattedValue: valueFormat('maxSpeed'),
      },
      acceleration: {
        value: data.acceleration?.value,
        unit: data.acceleration?.unit,
        formattedValue: valueFormat('acceleration'),
      },
      seats: {
        value: data.numberOfSeats,
      },
      doors: {
        value: data.numberOfDoors,
      },
      transmission: {
        transmissionCategory: {
          formattedValue: data.gearBox?.code
            ? translateProductEnum('gearBox', data.gearBox.code, data.gearBox?.name)
            : data.transmission,
          value: data.gearBox?.code,
        },
        gears: {
          formattedValue: String(data.numberOfGears),
          value: data.numberOfGears,
        },
      },
      payload: {
        unit: data.maxLoad?.unit,
        formattedValue: valueFormat('maxLoad'),
        value: data.maxLoad?.value,
      },
      weights: {
        maximumWeight: {
          unit: data.maxWeight?.unit,
          formattedValue: valueFormat('maxWeight'),
          value: data.maxWeight?.value,
        },
        unloadedWeight: {
          unit: data.weight?.unit,
          formattedValue: valueFormat('weight'),
          value: data.weight?.value,
        },
      },
      dimension: {
        unit: data.width?.unit,
        formattedValue: valueFormat('width'),
        value: data.width?.value,
      },
      engine: {
        driveConcept: undefined,
        cylinder: {
          formattedValue: String(data.numberOfCylinders),
          value: data.numberOfCylinders,
        },
        torque: {
          unit: data.torque?.unit,
          formattedValue: valueFormat('torque'),
          value: data.torque?.value,
        },
        fuelType: createOptionalFormattedData({
          formattedValue: data.fuelType?.code
            ? translateProductEnum('fuelType', data.fuelType.code, data.fuelType?.name)
            : getClassificationFeatureValue('.fuel_type'),
          value: data.fuelType?.code,
        }),
        typeOfPropulsion: {
          formattedValue: data.driveType?.code
            ? translateProductEnum('driveType', data.driveType.code, data.driveType?.name)
            : undefined,
          value: data.driveType?.code,
        },
        power: {
          combinedValue: {
            formattedValue: powerTranslation,
          },
          combustionKw: {
            value: data.combustionPower?.value,
            formattedValue: data.combustionPower?.displayValue,
            unit: data.combustionPower?.unit,
          },
          combustionHp: {
            value: data.combustionPowerSecondary?.value,
            formattedValue: data.combustionPowerSecondary?.displayValue,
            unit: data.combustionPowerSecondary?.unit,
          },
          combinedHp: {
            value: data.powerHpValue,
            formattedValue: getCombinedValue(data.powerHpValue, data.powerHpUnit),
            unit: data.powerHpUnit,
          },
          electricKw: {
            value: data.electricPower?.value,
            formattedValue: data.electricPower?.displayValue,
            unit: data.electricPower?.unit,
          },
          electricHp: {
            value: data.electricPowerSecondary?.value,
            formattedValue: data.electricPowerSecondary?.displayValue,
            unit: data.electricPowerSecondary?.unit,
          },
          combinedKw: {
            value: data.powerKwValue,
            formattedValue: getCombinedValue(data.powerKwValue, data.powerKwUnit),
            unit: data.powerKwUnit,
          },
        },
      },
    },
    emico: {
      wtlp: emicoData.wltp,
      nefz: emicoData.nefz,
    },
  };
}

const getCombinedValue = (value?: number, unit?: string) =>
  value && unit ? `${value} ${unit}` : value ? String(value) : undefined;

export function mapEmicoData(emicoData: any, lang: string): EmicoItem {
  const footnotes = emicoData?.footnotes?.[lang] as string;

  const attributes = (emicoData?.attributes ?? [])
    .map(
      (item: any) =>
        ({
          id: item.id,
          displayValue: item.displayValue,
          ...(item.displayImage && { displayImage: { url: item.displayImage[lang] } }),
          label: item.label && item.label[lang] ? item.label[lang] : '',
          order: item.order ?? 0,
          mustshowin: item.mustshowin ?? ['SHORTOVERVIEW', 'DETAILVIEW'],
        } as EmicoAttribute),
    )
    .sort((a, b) => a.order - b.order);

  return {
    attributes,
    footnotes,
  };
}
